import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    useTheme,
    useMediaQuery,
    Rating,
    Button,
    TextField,
    CircularProgress
} from '@mui/material';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';



const MobileAdminCardPreview = ({
    cards,
    currentIndex,
    onClose,
    onIndexChange,
    onSaveCard,
    saving
}) => {
    const [activeStep, setActiveStep] = useState(currentIndex);
    const [localRating, setLocalRating] = useState(0);
    const [localDescription, setLocalDescription] = useState('');
    const saveTimeoutRef = useRef(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);

    const currentCard = cards[activeStep];

    useEffect(() => {
        setActiveStep(currentIndex);
    }, [currentIndex]);

    useEffect(() => {
        if (currentCard) {
            setLocalRating(currentCard.rating || 0);
            setLocalDescription(currentCard.description || '');
        }
    }, [currentCard]);

    // Cleanup timeout przy odmontowywaniu
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);

    // Obsługa klawiszy strzałek
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'ArrowLeft') {
                handleBack();
            } else if (event.key === 'ArrowRight') {
                handleNext();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeStep, cards.length]);

    const handleNext = () => {
        if (activeStep < cards.length - 1) {
            const newIndex = activeStep + 1;
            setActiveStep(newIndex);
            onIndexChange?.(newIndex);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            const newIndex = activeStep - 1;
            setActiveStep(newIndex);
            onIndexChange?.(newIndex);
        }
    };

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;
        const diffX = touchStartX.current - touchEndX;
        const diffY = touchStartY.current - touchEndY;

        // Only handle horizontal swipes (ignore vertical swipes)
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
            if (diffX > 0) {
                // Swipe left - next card
                handleNext();
            } else {
                // Swipe right - previous card
                handleBack();
            }
        }
    };



    const handleDescriptionChange = (e) => {
        const newDescription = e.target.value;
        setLocalDescription(newDescription);

        // Automatyczne zapisywanie z opóźnieniem
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
            const updatedCard = {
                ...currentCard,
                rating: localRating,
                description: newDescription
            };
            onSaveCard(updatedCard);
        }, 1000); // 1 sekunda opóźnienia
    };

    if (!isMobile || !cards || cards.length === 0) {
        return null;
    }

    if (!currentCard) {
        return (
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.95)',
                    zIndex: 1300,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                    Błąd: Nie można załadować karty
                </Typography>
                <Button onClick={onClose} variant="outlined" sx={{ color: 'white', borderColor: 'white' }}>
                    Zamknij
                </Button>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.95)',
                zIndex: 1300,
                display: 'flex',
                flexDirection: 'column'
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1,
                borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                backdropFilter: 'blur(20px)'
            }}>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                    {activeStep + 1}/{cards.length}
                </Typography>
                <IconButton onClick={onClose} sx={{ color: 'white', p: 0.5 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Main content */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative'
            }}>
                {/* Card image */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    p: 2,
                    backgroundColor: 'rgba(17, 24, 39, 0.5)'
                }}>
                    {currentCard?.image_urls && currentCard.image_urls.length > 0 ? (
                        <img
                            src={currentCard.image_urls[0].url}
                            alt={currentCard.name}
                            style={{
                                width: '100%',
                                maxWidth: '300px',
                                height: 'auto',
                                borderRadius: '12px',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: '300px',
                                height: '420px',
                                background: 'linear-gradient(135deg, rgba(76, 29, 149, 0.2), rgba(139, 92, 246, 0.2))',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px dashed rgba(76, 29, 149, 0.3)',
                                borderRadius: '12px'
                            }}
                        >
                            <Typography variant="body1" sx={{ color: 'rgb(156, 163, 175)' }}>
                                Brak obrazka
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Card info */}
                <Box sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: 2,
                    backgroundColor: 'rgba(17, 24, 39, 0.8)'
                }}>

                    {/* Rating section */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Rating
                                value={localRating}
                                onChange={(event, newValue) => {
                                    setLocalRating(newValue);
                                    // Automatyczne zapisywanie przy zmianie oceny
                                    const updatedCard = {
                                        ...currentCard,
                                        rating: newValue,
                                        description: localDescription
                                    };
                                    onSaveCard(updatedCard);
                                }}
                                precision={0.5}
                                size="large"
                                sx={{
                                    '& .MuiRating-iconFilled': { color: 'rgb(245, 158, 11)' },
                                    '& .MuiRating-iconHover': { color: 'rgb(245, 158, 11)' }
                                }}
                            />
                            <Typography variant="body2" sx={{ color: 'white', ml: 1, fontWeight: 600 }}>
                                {localRating}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Description section */}
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            multiline
                            rows={3}
                            fullWidth
                            value={localDescription}
                            onChange={handleDescriptionChange}
                            placeholder="Opis (opcjonalnie)"
                            sx={{
                                '& .MuiInputLabel-root': { color: 'rgb(196, 181, 253)' },
                                '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    '& fieldset': { borderColor: 'rgba(76, 29, 149, 0.5)' },
                                    '&:hover fieldset': { borderColor: 'rgba(76, 29, 149, 0.7)' },
                                    '&.Mui-focused fieldset': { borderColor: 'rgb(76, 29, 149)' }
                                }
                            }}
                        />
                    </Box>

                    {/* Save status */}
                    {saving && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CircularProgress size={12} />
                                <Typography variant="caption" sx={{ color: 'rgb(156, 163, 175)' }}>
                                    Zapisywanie...
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Navigation arrows */}
            {activeStep > 0 && (
                <IconButton
                    onClick={handleBack}
                    sx={{
                        position: 'absolute',
                        left: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'rgba(255, 255, 255, 0.9)',
                        zIndex: 10,
                        width: 40,
                        height: 40,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            color: 'white'
                        }
                    }}
                >
                    <KeyboardArrowLeft sx={{ fontSize: '1.5rem' }} />
                </IconButton>
            )}

            {activeStep < cards.length - 1 && (
                <IconButton
                    onClick={handleNext}
                    sx={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'rgba(255, 255, 255, 0.9)',
                        zIndex: 10,
                        width: 40,
                        height: 40,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            color: 'white'
                        }
                    }}
                >
                    <KeyboardArrowRight sx={{ fontSize: '1.5rem' }} />
                </IconButton>
            )}
        </Box>
    );
};

export default MobileAdminCardPreview; 